import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EnableTotpResult } from "@autorepair/contracts";
import { authApi, ApiError } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { useNotice } from "../lib/useNotice";
import {
  ModernButton,
  ModernCard,
  ModernConfirmButton,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernNoticeBanner,
} from "./ui-modern";

/** One-time display of a fresh batch of backup codes — used both right
 * after enrollment and after a manual regenerate, so it's its own small
 * component rather than being duplicated in both call sites. */
function BackupCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <ModernModal title="Your Backup Codes" onCancel={onClose}>
      <p className="mb-3 text-[13.5px] text-[#4a5361]">
        Save these somewhere safe. Each code can be used once, instead of your authenticator app, if you ever lose
        access to your phone. They will not be shown again.
      </p>
      <div className="mb-4 grid grid-cols-2 gap-2 rounded-[10px] border border-[#e2e7ee] bg-[#fbfcfe] p-4 font-mono text-[14px] text-[#101b2d]">
        {codes.map((code) => (
          <div key={code}>{code}</div>
        ))}
      </div>
      <label className="mb-4 flex items-center gap-2 text-[13.5px] text-[#4a5361]">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="h-4 w-4 rounded border border-[#e2e7ee]"
        />
        I've saved these codes somewhere safe.
      </label>
      <ModernButton type="button" className="w-full" disabled={!acknowledged} onClick={onClose}>
        Done
      </ModernButton>
    </ModernModal>
  );
}

function EnrollModal({ onClose, onEnabled }: { onClose: () => void; onEnabled: (codes: string[]) => void }) {
  const [enrollment, setEnrollment] = useState<EnableTotpResult | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    authApi
      .enableTotp()
      .then(setEnrollment)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not start enrollment."))
      .finally(() => setStarting(false));
  }, []);

  const confirmMutation = useMutation({
    mutationFn: () => authApi.confirmTotp(code),
    onSuccess: (result) => onEnabled(result.backupCodes),
    onError: (err) => setError(err instanceof ApiError ? err.message : "Invalid or expired code."),
  });

  return (
    <ModernModal title="Enable Two-Factor Authentication" onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      {starting ? (
        <p className="text-[13.5px] text-[#4a5361]">Setting up…</p>
      ) : enrollment ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmMutation.mutate();
          }}
        >
          <p className="mb-3 text-[13.5px] text-[#4a5361]">
            Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password, etc.), or enter the
            key manually.
          </p>
          <div className="mb-3 flex justify-center">
            <img src={enrollment.qrCodeDataUri} alt="Two-factor authentication QR code" className="h-48 w-48" />
          </div>
          <p className="mb-4 break-all rounded-[10px] border border-[#e2e7ee] bg-[#fbfcfe] p-3 text-center font-mono text-[13.5px] text-[#101b2d]">
            {enrollment.secret}
          </p>
          <ModernField label="Enter the 6-digit code from your app" htmlFor="totp-confirm-code" required>
            <ModernInput
              id="totp-confirm-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </ModernField>
          <ModernButton type="submit" className="w-full" disabled={confirmMutation.isPending}>
            Confirm &amp; Enable
          </ModernButton>
        </form>
      ) : null}
    </ModernModal>
  );
}

function DisableModal({ onClose, onDisabled }: { onClose: () => void; onDisabled: () => void }) {
  const { session } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const disableMutation = useMutation({
    mutationFn: () => authApi.disableTotp(currentPassword, code),
    onSuccess: onDisabled,
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not disable two-factor authentication."),
  });

  // Chrome (and others) will otherwise autofill the saved account email
  // into the code field below — there's a password field in this form but
  // nothing marked autocomplete="username" for the browser to put it into
  // instead, so it guesses the nearest other text input. Invisible to the
  // user; only there to give autofill a proper target. See docs/TRAPS.md
  // for the matching backend fix (a malformed code must never crash the
  // request).
  const hiddenUsernameField = (
    <input type="text" name="email" autoComplete="username" value={session?.user.email ?? ""} readOnly hidden />
  );

  return (
    <ModernModal title="Disable Two-Factor Authentication" onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          disableMutation.mutate();
        }}
      >
        <p className="mb-3 text-[13.5px] text-[#4a5361]">
          This removes the extra sign-in step and revokes every "remembered" device. Confirm with your password and
          a current code (or a backup code).
        </p>
        {hiddenUsernameField}
        <ModernField label="Current password" htmlFor="disable-password" required>
          <ModernInput
            id="disable-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </ModernField>
        <ModernField label="Authenticator or backup code" htmlFor="disable-code" required>
          <ModernInput
            id="disable-code"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </ModernField>
        <ModernButton type="submit" variant="secondary" className="w-full" disabled={disableMutation.isPending}>
          Disable Two-Factor Authentication
        </ModernButton>
      </form>
    </ModernModal>
  );
}

export function TwoFactorAuthCard() {
  const { session, setSession } = useAuth();
  const queryClient = useQueryClient();
  const { notice, showSuccess, showError } = useNotice();
  const [showEnroll, setShowEnroll] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);

  const enabled = session?.user.totpEnabled ?? false;

  const devicesQuery = useQuery({
    queryKey: ["trusted-devices"],
    queryFn: () => authApi.listTrustedDevices(),
    enabled,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => authApi.regenerateBackupCodes(),
    onSuccess: (result) => setNewBackupCodes(result.codes),
    onError: (err) => showError(err instanceof ApiError ? err.message : "Could not regenerate backup codes."),
  });

  const revokeMutation = useMutation({
    mutationFn: (deviceId: number) => authApi.revokeTrustedDevice(deviceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trusted-devices"] });
      showSuccess("Device removed.");
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : "Could not remove that device."),
  });

  function setTotpEnabled(value: boolean): void {
    if (session) setSession({ ...session, user: { ...session.user, totpEnabled: value } });
  }

  const modals = (
    <>
      {showEnroll && (
        <EnrollModal
          onClose={() => setShowEnroll(false)}
          onEnabled={(codes) => {
            setShowEnroll(false);
            setTotpEnabled(true);
            setNewBackupCodes(codes);
            showSuccess("Two-factor authentication is now enabled.");
          }}
        />
      )}
      {showDisable && (
        <DisableModal
          onClose={() => setShowDisable(false)}
          onDisabled={() => {
            setShowDisable(false);
            setTotpEnabled(false);
            showSuccess("Two-factor authentication has been disabled.");
          }}
        />
      )}
      {newBackupCodes && <BackupCodesModal codes={newBackupCodes} onClose={() => setNewBackupCodes(null)} />}
    </>
  );

  return (
    <ModernCard className="mb-4">
      <h2 className="m-0 mb-3 text-[15px] font-bold text-[#071a33]">Two-factor authentication</h2>
      <ModernNoticeBanner notice={notice} />
      {enabled ? (
        <>
          <p className="mb-4 text-[13.5px] text-[#4a5361]">
            Two-factor authentication is <span className="font-semibold text-[#071a33]">enabled</span>. You'll be
            asked for a code from your authenticator app when signing in from a new device.
          </p>
          <div className="mb-4 flex flex-wrap gap-2.5">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              Regenerate Backup Codes
            </ModernButton>
            <ModernButton type="button" variant="secondary" onClick={() => setShowDisable(true)}>
              Disable
            </ModernButton>
          </div>

          <h3 className="mb-2 text-[13.5px] font-bold text-[#071a33]">Remembered devices</h3>
          {devicesQuery.isLoading ? (
            <p className="text-[13.5px] text-[#4a5361]">Loading…</p>
          ) : devicesQuery.data && devicesQuery.data.devices.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {devicesQuery.data.devices.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-[10px] border border-[#e2e7ee] bg-[#fbfcfe] p-3 text-[13.5px] text-[#101b2d]"
                >
                  <div>
                    <div>
                      {d.deviceLabel}
                      {d.location ? <span className="text-[#6b7a8f]"> · {d.location}</span> : null}
                    </div>
                    <div className="text-[12px] text-[#8b97a8]">
                      Remembered {new Date(d.createdAt).toLocaleDateString()} — expires{" "}
                      {new Date(d.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <ModernConfirmButton
                    variant="secondary"
                    className="min-h-0 px-3 py-1.5 text-[12.5px]"
                    confirmText="Remove this device?"
                    onConfirm={() => revokeMutation.mutate(d.id)}
                  >
                    Remove
                  </ModernConfirmButton>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13.5px] text-[#4a5361]">No devices are currently remembered.</p>
          )}
        </>
      ) : (
        <>
          <p className="mb-4 text-[13.5px] text-[#4a5361]">
            Add an extra step at sign-in using an authenticator app on your phone.
          </p>
          <ModernButton type="button" onClick={() => setShowEnroll(true)}>
            Enable Two-Factor Authentication
          </ModernButton>
        </>
      )}

      {modals}
    </ModernCard>
  );
}
